import { TokenManager } from '@sparky/token-manager'
import StartupManager from './StartupManager'
import { getParam, getSession, getAdminClusterURL } from './ui-lib/utils'
import { hasTsgSupport } from '../../framework/tsg'
import { __error } from '../../init/debug'
import type { AuthData, Credentials } from '@sparky/token-manager'
import type { AuthInfo, GetTokenManager, LoggedInUser } from '../../framework/stateTypes'

let tokenManager: TokenManager | null = null
let authData: AuthData | null = null
const reportuuids = {}

export const getToken = () => {
  if (hasTsgSupport()) {
    console.error('Token service no longer available in the IAM only env, please use IAM token!')
    throw new Error('Token service no longer available in the IAM only env')
  }
  const tenantId = getParam('tenantId')
  const token = getSession(tenantId)
  return token || undefined
}

export const getAuthData = (tenant: any, authInfo: AuthInfo, loggedInUser: LoggedInUser) => {
  // If authData exist, use it
  if (authData) {
    return authData
  }

  if (!authInfo) {
    console.error('getAuthData called when authInfo is not available')
    return authData
  }

  const { currentSupportAccountId, email, profile, supportAccountIds } = loggedInUser || {}
  const { firstname, lastname, displayName, supportaccountids } = profile || {}
  const accounts = supportAccountIds?.length ? supportAccountIds : (supportaccountids && JSON.parse(supportaccountids)) || []
  const accountId = +(currentSupportAccountId || 0)

  // accountInfo
  const accountInfo = {
    index: 0,
    accountId,
    accountName: 'Palo Alto Networks',
  }
  for (let i = 0; i < accounts.length; i++) {
    const item = accounts[i]
    if (item.accountid === accountId) {
      accountInfo.index = i
      accountInfo.accountName = item.name
      break
    }
  }

  const companyName = accountInfo.accountName
  const userName = `${firstname} ${lastname}`.trim() || email
  // userData
  const userData = {
    userName,
    companyName,
    email,
    displayName,
    firstName: firstname,
    lastName: lastname,
  }

  // authData
  authData = {
    credentials: authInfo.credentials as Credentials,
    entitlements: authInfo.entitlements,
    userData,
    accountInfo,
  }
  return authData
}

export const getTokenManager: GetTokenManager = (tenant, authInfo, loggedInUser, featuresInfo = []) => {
  if (hasTsgSupport()) {
    __error('Token service no longer available in the IAM only env, please use IAM token!')
    return
  }
  // If tokenManager exist, use it
  if (tokenManager) {
    return tokenManager
  }

  // If no entitlements in authInfo, we should not continue
  if (!authInfo?.entitlements) {
    console.error('The authInfo is not available or no entitlements inside it')
    return
  }

  // managedAppName = 'prisma_access' || 'prisma_access_panorama'
  const managedAppName = StartupManager.getManagedAppName()

  // token manager
  const APP_NAME = 'Fawkes'
  const MAX_NUMBER_OF_TENANTS_PER_APP = new Map([[managedAppName, 1]])

  const mandatoryAssociations = new Map([[managedAppName, new Set(['logging_service'])]])
  const optionalAssociations = new Map([[managedAppName, new Set(['directory_sync'])]])

  const EXPIRATION_CHECK_INTERVAL = 120 // seconds
  const TIME_BEFORE_EXPIRATION = 180 // seconds
  const NUMBER_OF_RETRY_FOR_JWT = 5
  const API_SERVER_FQDN = `${getAdminClusterURL()}/api/xseer/getToken`
  const jwToken = getToken()
  TokenManager.init(
    APP_NAME,
    MAX_NUMBER_OF_TENANTS_PER_APP,
    EXPIRATION_CHECK_INTERVAL,
    TIME_BEFORE_EXPIRATION,
    NUMBER_OF_RETRY_FOR_JWT,
    true, // association or not
    mandatoryAssociations,
    optionalAssociations,
    API_SERVER_FQDN,
    jwToken,
  )
  tokenManager = TokenManager.getInstance(APP_NAME)

  // authData
  const authData = getAuthData(tenant, authInfo, loggedInUser)
  if (authData) {
    try {
      tokenManager.setAuth(authData)
    }
    catch (e) {
      console.log(e)
    }
  }

  return tokenManager
}

export const getReportuuids = () => {
  return reportuuids
}

export const extractConfigComposed = (tenant: any, authInfo: AuthInfo, loggedInUser: LoggedInUser, featuresInfo = []) => {
  if (!authInfo?.entitlements) {
    // no entitlements inside authInfo
    return {}
  }

  // FQDNs
  const fqdns = StartupManager.getReportingFQDNs()

  const apiBaseURL = fqdns.apiServer // "https://ingress.distg.cloud.pan.local:443/";
  const baseRoute = '/reports'

  // config
  const config = {
    apiBaseURL,
    baseRoute,
    crossLinks: {},
    preloadedState: {}
  }

  return config
}

export const extractAuthDataComposed = (tenant: any, authInfo: AuthInfo, loggedInUser: LoggedInUser, featuresInfo = []) => {
  if (!authInfo || !authInfo.entitlements) {
    // no entitlements inside authInfo
    return {}
  }

  // appPortalUrl
  const appPortalUrl = StartupManager.getAppPortalUrl(authInfo)

  // config
  const config = extractConfigComposed(tenant, authInfo, loggedInUser, featuresInfo)

  // token manager
  const tokenManager = getTokenManager(tenant, authInfo, loggedInUser)
  const authData = getAuthData(tenant, authInfo, loggedInUser)
  const authDataComposed = {
    authData,
    config,
    appPortalUrl,
    tokenManager,
  }
  return authDataComposed
}
